import { Pressable, ScrollView, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import type { LucideIcon } from 'lucide-react-native';

import { colors, radius, typography } from '@/theme/tokens';

export interface TabItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  badge?: string | number;
  badgeTone?: 'magenta' | 'success' | 'warning' | 'danger' | 'neutral';
}

interface Props {
  items: TabItem[];
  active: string;
  onChange: (id: string) => void;
  style?: StyleProp<ViewStyle>;
}

const BADGE_BG: Record<NonNullable<TabItem['badgeTone']>, { bg: string; fg: string }> = {
  magenta: { bg: colors.magenta[100], fg: colors.magenta[800] },
  success: { bg: colors.success[50], fg: colors.success[600] },
  warning: { bg: colors.warning[50], fg: colors.warning[600] },
  danger: { bg: colors.danger[50], fg: colors.danger[600] },
  neutral: { bg: colors.ink[100], fg: colors.ink[700] },
};

export function Tabs({ items, active, onChange, style }: Props) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 0 }}
    >
      <View
        style={[
          {
            flexDirection: 'row',
            gap: 4,
            padding: 5,
            backgroundColor: colors.white,
            borderRadius: radius.full,
            borderWidth: 1,
            borderColor: colors.ink[100],
            alignSelf: 'flex-start',
          },
          style,
        ]}
      >
      {items.map((it) => {
        const isActive = it.id === active;
        const Icon = it.icon;
        const badge = it.badge !== undefined ? String(it.badge) : null;
        const tone = it.badgeTone ?? 'neutral';
        const badgePalette = BADGE_BG[tone];
        return (
          <Pressable
            key={it.id}
            onPress={() => onChange(it.id)}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              paddingHorizontal: 14,
              paddingVertical: 8,
              borderRadius: 999,
              backgroundColor: isActive ? colors.magenta[700] : hovered ? colors.crema[50] : 'transparent',
              // @ts-ignore web only
              cursor: 'pointer',
              // @ts-ignore web only
              transitionProperty: 'background-color',
              // @ts-ignore web only
              transitionDuration: '160ms',
            })}
          >
            {Icon ? (
              <Icon size={15} color={isActive ? colors.white : colors.ink[700]} />
            ) : null}
            <Text
              style={{
                color: isActive ? colors.white : colors.ink[700],
                fontWeight: typography.weights.bold,
                fontSize: 13,
              }}
            >
              {it.label}
            </Text>
            {badge ? (
              <View
                style={{
                  backgroundColor: isActive ? 'rgba(255,255,255,0.22)' : badgePalette.bg,
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 999,
                  minWidth: 20,
                  alignItems: 'center',
                }}
              >
                <Text
                  style={{
                    color: isActive ? colors.white : badgePalette.fg,
                    fontSize: 10,
                    fontWeight: typography.weights.bold,
                  }}
                >
                  {badge}
                </Text>
              </View>
            ) : null}
          </Pressable>
        );
      })}
      </View>
    </ScrollView>
  );
}
